import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { ChevronLeft, ChevronRight, Package } from "lucide-react";

export default function CategoryCarousel() {
  const settings = useSettings();
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const trackRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  useEffect(() => {
    api.get("/categories/homepage").then((r) => setCats(r.data)).catch(() => setCats([]));
  }, []);

  const updateArrows = () => {
    const el = trackRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateArrows();
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => { el.removeEventListener("scroll", updateArrows); window.removeEventListener("resize", updateArrows); };
  }, [cats]);

  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.85), behavior: "smooth" });
  };

  if (settings && settings.homepage_categories_enabled === false) return null;
  if (!cats.length) return null;

  return (
    <section data-testid="homepage-category-block" className="border-b bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg md:text-xl font-bold text-steel">Shop by Category</h2>
        </div>
        <div className="relative">
          {canLeft && (
            <button data-testid="category-scroll-left" onClick={() => scrollBy(-1)} aria-label="Scroll left"
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 grid place-items-center w-10 h-10 rounded-full bg-white shadow-md border hover:bg-accent hover:text-steel transition-colors">
              <ChevronLeft size={22} />
            </button>
          )}
          <div ref={trackRef}
            className="flex gap-4 overflow-x-auto scroll-smooth px-1 py-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            {cats.map((c) => (
              <button key={c.id} data-testid={`homepage-category-${c.slug}`}
                onClick={() => navigate(`/category/${c.slug}`)}
                className="group shrink-0 basis-[42%] sm:basis-[30%] md:basis-[19%] lg:basis-[calc((100%-2rem)/9)] flex flex-col items-center text-center">
                <span className="w-full aspect-square rounded-xl border bg-slate-100 grid place-items-center overflow-hidden group-hover:border-accent transition-colors">
                  {c.image_url
                    ? <img src={c.image_url} alt={c.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    : <Package size={26} className="text-slate-300" />}
                </span>
                <span className="mt-2 text-xs md:text-sm font-semibold text-steel line-clamp-2 leading-snug group-hover:text-accent transition-colors">{c.name}</span>
              </button>
            ))}
          </div>
          {canRight && (
            <button data-testid="category-scroll-right" onClick={() => scrollBy(1)} aria-label="Scroll right"
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 grid place-items-center w-10 h-10 rounded-full bg-white shadow-md border hover:bg-accent hover:text-steel transition-colors">
              <ChevronRight size={22} />
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
